'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, lazy, Suspense, useEffect } from 'react'
import { logger } from '@/lib/logger'

// Lazy load devtools - only imported in development
const ReactQueryDevtools = lazy(() =>
  import('@tanstack/react-query-devtools').then((mod) => ({
    default: mod.ReactQueryDevtools,
  }))
)

// Client-only wrapper to prevent hydration mismatch with DevTools
function ClientOnlyDevtools() {
  const [mounted, setMounted] = useState(false)

  // This pattern is intentional - we need to detect client-side mount
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setMounted(true)
  }, [])
  /* eslint-enable react-hooks/set-state-in-effect */

  if (!mounted) return null

  return (
    <Suspense fallback={null}>
      <ReactQueryDevtools
        initialIsOpen={false}
        position="bottom"
        buttonPosition="bottom-left"
      />
    </Suspense>
  )
}

/**
 * React Query Provider
 *
 * Provides QueryClient to the entire application with optimized defaults.
 *
 * Best Practices:
 * - Create QueryClient inside component to avoid sharing between users (SSR)
 * - Configure default options for consistent behavior
 * - Enable devtools in development only (lazy loaded)
 */
export default function QueryProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Time data is considered fresh (no refetch) - OPTIMIZED
            staleTime: 5 * 60 * 1000, // 5 minutes (was 1 minute)

            // Time inactive queries stay in cache - lower for low-memory devices (Chromebooks, etc.)
            gcTime: 5 * 60 * 1000, // 5 minutes

            // Retry logic
            retry: (failureCount, error) => {
              // Don't retry on 4xx errors (client errors)
              const errorWithStatus = error as { status?: number }
              if (
                errorWithStatus?.status &&
                errorWithStatus.status >= 400 &&
                errorWithStatus.status < 500
              ) {
                return false
              }
              // Retry 5xx errors up to 2 times
              return failureCount < 2
            },

            // Refetch behaviors - OPTIMIZED to reduce unnecessary requests
            refetchOnWindowFocus: false, // DISABLED to reduce memory churn
            refetchOnReconnect: false, // DISABLED to prevent unnecessary refetches
            refetchOnMount: false, // DISABLED to prevent unnecessary refetches

            // Network mode
            networkMode: 'online', // Only fetch when online

            // Performance optimizations
            structuralSharing: true, // Reduce memory by reusing unchanged data
          },
          mutations: {
            // Don't retry user errors (400-499), only system errors
            retry: (failureCount, error) => {
              // Don't retry on 4xx errors (client/user errors)
              const errorWithStatus = error as { status?: number }
              if (
                errorWithStatus?.status &&
                errorWithStatus.status >= 400 &&
                errorWithStatus.status < 500
              ) {
                return false
              }
              // Retry 5xx errors once
              return failureCount < 1
            },

            // Network mode for mutations
            networkMode: 'online',

            // Global error handling for mutations - report system errors to Sentry
            onError: (error) => {
              // Only report system errors (500+) to Sentry, not user errors (400-499)
              const errorWithStatus = error as {
                status?: number
                isUserError?: boolean
              }
              if (
                !errorWithStatus?.isUserError &&
                errorWithStatus?.status &&
                errorWithStatus.status >= 500
              ) {
                logger.error('Mutation error (system):', error)
              }
            },
          },
        },
      })
  )

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* React Query Devtools - client-only to prevent hydration mismatch */}
      {process.env.NODE_ENV === 'development' && <ClientOnlyDevtools />}
    </QueryClientProvider>
  )
}
